<script lang="ts">
	// The site's header, the same on every page: the mark, the sections of the
	// home page, Pricing, and the way in. As "Home Visibility" draws it, with
	// one difference the owner asked for on 2026-10-03: it stays at the top
	// while the page scrolls.

	import BrandMark from '../components/BrandMark.svelte';
	import Button from '../components/Button.svelte';
	import { brand, nav } from './copy';
	import { signUpHref } from './links';

	interface Props {
		page: 'home' | 'pricing' | 'privacy';
	}

	let { page }: Props = $props();

	const links = [
		{ href: '/#reports', label: nav.reports, testId: 'site-nav-reports' },
		{ href: '/#summaries', label: nav.summaries, testId: 'site-nav-summaries' },
		{ href: '/#orders', label: nav.orders, testId: 'site-nav-orders' },
	];
</script>

<header class="header">
	<a class="brand" href="/" aria-label={nav.home} data-testid="site-nav-home">
		<BrandMark />
		<span class="name">{brand}</span>
	</a>
	<nav class="nav" aria-label={nav.label}>
		{#each links as link (link.testId)}
			<a href={link.href} data-testid={link.testId}>{link.label}</a>
		{/each}
		<a
			href="/pricing"
			class:current={page === 'pricing'}
			aria-current={page === 'pricing' ? 'page' : undefined}
			data-testid="site-nav-pricing">{nav.pricing}</a
		>
	</nav>
	<div class="start">
		<Button testId="site-start" tone="ink" size="sm" label={nav.start} href={signUpHref} />
	</div>
</header>

<style>
	/* Sticky over the page's own ground, so what scrolls under it is hidden
	   rather than showing through. Above the demos' own layers (the till
	   panel's empty state sits at 1), and the site has no Sheet or Notice
	   for it to cover. */
	.header {
		position: sticky;
		inset-block-start: 0;
		z-index: 10;
		display: flex;
		align-items: center;
		gap: var(--space-7);
		flex-wrap: wrap;
		padding-block: var(--space-8) var(--space-6);
		background: var(--surface-page);
		border-block-end: var(--border-width) solid var(--border-default);
	}

	.brand {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		flex: none;
		color: var(--text-strong);
		text-decoration: none;
	}

	.name {
		font-size: var(--text-h4);
		font-weight: var(--weight-bold);
		letter-spacing: var(--tracking-snug);
	}

	.nav {
		display: flex;
		gap: var(--space-8);
		flex-wrap: wrap;
		flex: none;
		font-size: var(--text-xs);
		font-weight: var(--weight-medium);
	}

	.nav a {
		color: var(--text-body);
		white-space: nowrap;
	}

	.nav a.current {
		color: var(--text-strong);
		border-block-end: 2px solid var(--speed);
	}

	.start {
		display: flex;
		align-items: center;
		flex: none;
		margin-inline-start: auto;
	}

	/* On a phone, at the width NavMenu names, the three parts would stack into
	   three rows and a sticky header would hold a fifth of the screen. The way
	   in moves up beside the mark, and the links take the second row. */
	@media (max-width: 639.98px) {
		.header {
			gap: var(--space-4) var(--space-7);
			padding-block: var(--space-5) var(--space-4);
		}

		.start {
			order: 1;
		}

		.nav {
			order: 2;
			flex-basis: 100%;
			gap: var(--space-7);
		}
	}
</style>
